<script setup>
import { ref, defineProps, computed } from "vue";

import dayjs from 'dayjs'
import EquipementsHistories from './EquipmentsHistories.vue';
import EquipmentStatus from "./EquipmentStatus.vue";
import { VueSelecto } from "vue3-selecto";
import History from "@/components/equipment/History.vue";
import Tooltip from '@/components/ui/Tooltip.vue'
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue";
import { cn } from "@/shadcn/lib/utils.js";

const props = defineProps({
  equipments: {
    type: Array,
    default: () => []
  },
  parks: {
    type: Array,
    default: () => []
  },
  year : {
    default: () => (new Date()).getFullYear()
  },
  month : {
    default: () => (new Date()).getMonth()
  },
  allotmentResource: {
    type: String,
    default: '',
  },
  equipmentStatuses: {
    type: Array,
    default: () => []
  },
  tourFilters: {
    type: Object,
    default: () => ({})
  },
  timezones: {
    type: Array,
    default: () => []
  },
  canEditCalendar: {
    type: Boolean,
    default: false
  },
  hideEquipment: {
    type: Boolean,
    default: false
  }
});

const currentYear = ref(props.year);
const currentMonth = ref(props.month);

const timezone = ref(Intl.DateTimeFormat().resolvedOptions().timeZone);
const onTimezoneChange = (tz) => {
  timezone.value = tz;
}

const onPeriodChange = ({ year, month }) => {
  currentYear.value = year;
  currentMonth.value = month;
}

const daysInMonth = () => {
  return 32 - new Date(Date.UTC(currentYear.value, currentMonth.value, 32)).getDate();
}

const days = computed(() => {
  return Array.from(Array(daysInMonth()).keys())
    .map((day) => {
      const dayjsLocal = dayjs(currentYear.value + '-' + (currentMonth.value + 1) + '-' + (day + 1));
      return dayjsLocal.tz(timezone.value)
    })
});

const onSelect = (e) => {
  e.added.forEach(el => {
    el.classList.add("bg-primary");
  });
  e.removed.forEach(el => {
    el.classList.remove("bg-primary");
  });
}

const window = ref(null);

const onSelectEnd = (e) => {
  handleSelection(
    e.selected.map(el => el.dataset.coord)
  )
}

const equipmentHistory = ref(null);
const handleSelection = (selection) => {
  if (selection.length === 0) {
    return;
  }
  let dayIndexes = new Set();
  let equipmentIndexes = new Set();
  selection.forEach(element => {
    const [day, equipment] = element.split(':');
    dayIndexes.add(day);
    equipmentIndexes.add(equipment);
  });

  dayIndexes = Array.from(dayIndexes);
  equipmentIndexes = Array.from(equipmentIndexes);

  const selectedEquipments = props.equipments.slice(parseInt(equipmentIndexes[0]), parseInt(equipmentIndexes.slice(-1)) + 1);
  const selectedStart = dayjs.tz(days.value[parseInt(dayIndexes[0])], timezone.value).startOf('day');
  const selectedEnd = dayjs.tz(days.value[parseInt(dayIndexes.slice(-1))], timezone.value).endOf('day');

  equipmentHistory.value.onSelection(selectedEquipments, selectedStart, selectedEnd);
}

const onResetAllotment = () => {
  document.querySelectorAll(".calendar-day").forEach(el => {
    el.classList.remove("bg-primary");
  });
}
</script>
<template>
<div>
  <EquipementsHistories
    :timezones="timezones"
    :equipments="equipments"
    :tourFilters="tourFilters"
    :year="currentYear"
    :month="currentMonth"
    :parks="parks"
    v-slot:default="{
      isLoading: isLoadingHistories,
      histories,
      calendarEvents,
      onClickHistory
    }"
    ref="equipmentHistory"
    :equipmentStatuses="equipmentStatuses"
    :allotmentResource="allotmentResource"
    @onResetAllotment="onResetAllotment"
    @onPeriodChange="onPeriodChange"
    @onTimezoneChange="onTimezoneChange"
  >
    <VueSelecto
      dragContainer=".calendar"
      :selectableTargets='[".calendar-day"]'
      :selectByClick="canEditCalendar"
      :selectFromInside="canEditCalendar"
      :continueSelect="false"
      :toggleContinueSelect='"shift"'
      :keyContainer="window"
      :hitRate="2"
      @select="onSelect"
      @selectEnd="onSelectEnd"
    />

    <div class="container-fluid d-flex justify-content-center align-items-center mb-3">
      <LoadingOverlay :active="isLoadingHistories">
        <div class="calendar"
             :style="{
            gridTemplateColumns: hideEquipment ? 'repeat(31, minmax(35px, 55px))' : '250px repeat(32, minmax(35px, 55px))',
          }">
          <div v-if="!hideEquipment"
               class="calendar-item tw:bg-white tw:font-bold d-flex align-items-center"
               :style="{
              gridColumn: 1,
              gridRow: 1,
             }">
            {{ $t('Model') }}
          </div>
          <div v-if="!hideEquipment"
               class="calendar-item tw:bg-white d-flex align-items-center justify-content-center"
               :style="{
                gridColumn: 2,
                gridRow: 1,
             }">
            <Tooltip title="Equipment status">
              <i class="fa fa-info-circle"></i>
            </Tooltip>
          </div>
          <div v-for="(day, i) in days" :key="i" :class="cn(
              'calendar-item',
              'p-1 d-flex align-content-center flex-column text-center',
              'tw:bg-white',
              [0,6].includes(day.day()) ? 'tw:bg-zinc-50 tw:text-zinc-500' : 'tw:font-semibold',
              day.isSame(dayjs(), 'day') ? 'tw:bg-amber-50' : '',
          )" style="width: 100%; border-color: #dee2e6">
            <span>{{ day.date() }}</span>
            <small :class="[
                [0,6].includes(day.day()) ? 'tw:text-zinc-500' : '',
                'd-block'
              ]">{{ day.format('ddd') }}</small>
          </div>

          <template v-for="(equipment, equipmentIndex) in equipments"
          >
            <div v-if="!hideEquipment"
                 class="calendar-item tw:bg-white d-flex flex-column align-items-start justify-content-center"
                 :style="{
                 gridColumn: 1,
                 gridRow: equipmentIndex + 2,
              }"
            >
              <a :href="equipment.links.show"
                 target="_blank"
              >
                <small class="d-block text-truncate">
                  <strong>{{ equipment.product.name }}</strong>
                  ({{ equipment.product.year }})
                </small>
                <small class="d-flex align-items-center flex-nowrap">
                  <span class="d-block text-truncate text-muted font-weight-bold">{{ equipment.size }} <template v-if="equipment.size &&  equipment.color">-</template> {{ equipment.color }}</span>
                  <i class="d-block fa fa-barcode ml-2 mr-1"></i>
                  <span class="d-block text-truncate" style="direction: rtl;">{{ equipment.barcode_number }}</span>
                </small>
              </a>
            </div>
            <a v-if="!hideEquipment"
               :href="equipment.links.show"
               target="_blank"
               class="calendar-item tw:bg-white d-flex align-items-center justify-content-center"
               :style="{
                gridColumn: 2,
                gridRow: equipmentIndex + 2,
             }"
            >
              <EquipmentStatus :status="equipment.status" />
            </a>

            <div v-for="(day, dayIndex) in days"
                 :key="day.format('YYYYMMDD') + '.' + equipmentIndex"
                 :class="cn(
                'position-relative',
                'calendar-item calendar-day px-1',
                'd-flex align-content-center align-items-center',
                'tw:bg-white',
                [0,6].includes(day.day()) ? 'tw:bg-zinc-50' : '',
                day.isSame(dayjs(), 'day') ? 'tw:bg-amber-50' : '',
              )"
                 :style="{
                  gridColumn: dayIndex + (hideEquipment ? 1 : 3),
                  gridRow: equipmentIndex + 2,
                }"
                 :data-coord="dayIndex + ':' + equipmentIndex"
            >
              <div class="position-absolute" style="font-size: 0.5em; top: 0; right: 0; color:#bdbdbd; z-index: 7;" >{{ day.date() }}</div>
            </div>
          </template>

          <History v-for="history in histories"
                   :key="history.id"
                   :history="history"
                   :timezone="timezone"
                   :calendar-start="days[0]"
                   :calendar-end="days[days.length - 1].endOf('day')"
                   :columnOffset="hideEquipment ? 0 : 2"
                   :grid-row="equipments.findIndex(e => e.id === history.equipment_id) + 2"
                   @click="onClickHistory(history)"
          />
        </div>
      </LoadingOverlay>
    </div>
  </EquipementsHistories>
</div>
</template>
<style scoped>
.calendar{
  display: grid;
  grid-template-rows: 55px;
  grid-auto-rows: 55px;
  gap: 0;
  overflow: auto;
}
.calendar-sm {
  /* grid-template-columns: repeat(39, minmax(20px, 1fr));*/
}
.calendar-item {
  border-top: 1px solid #DEE2E9;
  border-bottom: 1px solid #DEE2E9;
}

.calendar-day{
  border-left: 1px solid #f5f6f8;
}
</style>
